import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { CreditCard, CheckCircle, Loader2, X, QrCode, Download, Link2, Copy, Check, Send } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  paid: "border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8",
  pending: "border-[#F59E0B]/30 text-[#D97706] bg-[#F59E0B]/8",
  overdue: "border-[#FF3636]/30 text-[#DC2626] bg-[#FF3636]/8",
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function Fees() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [showQR, setShowQR] = useState(null);     // { order_id, qr_code_url, amount, fees }
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [paying, setPaying] = useState(null);       // fee ID being paid
  const [selected, setSelected] = useState([]);     // multi-select fee IDs
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);
  const [form, setForm] = useState({ student_name: "", student_email: "", fee_type: "Tuition Fee", amount: "", semester: 5, due_date: "2026-03-31" });

  // Payment link state
  const [linkTab, setLinkTab] = useState("fee");
  const [linkFeeId, setLinkFeeId] = useState("");
  const [linkEmail, setLinkEmail] = useState("");
  const [linkResult, setLinkResult] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { fetchAll(); fetchConfig(); }, []);

  const fetchAll = async () => {
    try {
      const [d, s] = await Promise.all([
        axios.get(`${API}/fees`, { withCredentials: true }),
        axios.get(`${API}/fees/stats`, { withCredentials: true }),
      ]);
      setData(d.data); setStats(s.data);
    } catch {} finally { setLoading(false); }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/fees/config`);
      setRazorpayKeyId(res.data.key_id);
    } catch {}
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openCheckout = useCallback(async (feeIds) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) { showToast("Failed to load payment SDK", "error"); return; }

    setPaying(feeIds[0]);
    try {
      const { data: order } = await axios.post(`${API}/fees/create-order`, { fee_ids: feeIds, type: "checkout" }, { withCredentials: true });

      const options = {
        key: razorpayKeyId || order.razorpay_key_id,
        amount: order.amount,
        currency: "INR",
        name: "Acadence",
        description: `Fee Payment (${feeIds.length} item${feeIds.length > 1 ? "s" : ""})`,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/fees/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { withCredentials: true });
            showToast("Payment successful!");
            setSelected([]);
            fetchAll();
          } catch {
            showToast("Payment verification failed. Contact support if charged.", "error");
          }
        },
        modal: {
          ondismiss: () => showToast("Payment cancelled", "error"),
        },
        prefill: { email: user?.email || "" },
        theme: { color: "#0C0C10" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      showToast("Failed to create payment order", "error");
    } finally { setPaying(null); }
  }, [razorpayKeyId, user]);

  const handlePay = (id) => openCheckout([id]);
  const handlePaySelected = () => { if (selected.length) openCheckout(selected); };

  const handleQRPay = async (feeIds) => {
    try {
      const { data: order } = await axios.post(`${API}/fees/create-order`, { fee_ids: feeIds, type: "qr" }, { withCredentials: true });
      setShowQR({ order_id: order.order_id, qr_code_url: order.qr_code_url, amount: order.amount });

      // Start polling
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: status } = await axios.get(`${API}/fees/order-status/${order.order_id}`, { withCredentials: true });
          if (status.status === "paid") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setShowQR((prev) => prev ? { ...prev, paid: true } : null);
            showToast("QR Payment successful!");
            setSelected([]);
            fetchAll();
          }
        } catch {}
      }, 5000);

      // Auto-stop after 10 minutes
      setTimeout(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 600000);
    } catch {
      showToast("Failed to generate QR code", "error");
    }
  };

  const closeQR = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setShowQR(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/fees`, { ...form, amount: parseFloat(form.amount) }, { withCredentials: true });
      setShowModal(false);
      fetchAll();
    } catch {}
  };

  const handleCreateLink = async () => {
    setLinkLoading(true);
    try {
      const body = linkTab === "fee"
        ? { type: "fee", fee_id: linkFeeId }
        : { type: "student", student_email: linkEmail };
      const { data: res } = await axios.post(`${API}/fees/payment-link`, body, { withCredentials: true });
      setLinkResult(res);
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to create link", "error");
    } finally { setLinkLoading(false); }
  };

  const copyLink = () => {
    if (linkResult?.short_url) {
      navigator.clipboard.writeText(linkResult.short_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleSelect = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const unpaidItems = data.filter((d) => d.status !== "paid");
  const allSelected = unpaidItems.length > 0 && unpaidItems.every((d) => selected.includes(d._id));
  const toggleAll = () => setSelected(allSelected ? [] : unpaidItems.map((d) => d._id));
  const selectedTotal = data.filter((d) => selected.includes(d._id)).reduce((sum, d) => sum + (d.amount || 0), 0);

  const filtered = filter === "all" ? data : data.filter((d) => d.status === filter);
  const statCards = [
    { label: "Total Collected", value: `₹${((stats.total_collected || 0) / 100000).toFixed(1)}L`, color: "#22C55E" },
    { label: "Pending Amount", value: `₹${((stats.total_pending || 0) / 100000).toFixed(1)}L`, color: "#F59E0B" },
    { label: "Overdue / Defaulters", value: stats.defaulters || 0, color: "#FF3636" },
    { label: "Paid Records", value: stats.paid_count || 0, color: "#0A7AFF" },
  ];

  // Unique student emails from pending fees (for payment link student tab)
  const pendingStudents = [...new Set(data.filter((d) => d.status !== "paid").map((d) => d.student_email))];

  return (
    <div className="space-y-6" data-testid="fees-module">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Finance</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Fees Collection</h2>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <button onClick={() => { setShowLinkModal(true); setLinkResult(null); setLinkFeeId(""); setLinkEmail(""); }} data-testid="generate-link-btn"
              className="border border-[#D8D4CB] text-[#0C0C10] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-4 py-3 hover:bg-[#0C0C10] hover:text-[#EAFF00] hover:border-[#0C0C10] transition-all flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" /> Payment Link
            </button>
          )}
          {user?.role === "admin" && (
            <button onClick={() => setShowModal(true)} data-testid="add-fee-btn"
              className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
              <CreditCard className="w-3.5 h-3.5" /> Add Record
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["all", "paid", "pending", "overdue"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`fee-filter-${f}`}
            className={`font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-4 py-2 border transition-all ${filter === f ? "bg-[#0C0C10] text-[#EAFF00] border-[#0C0C10]" : "border-[#D8D4CB] text-[#7A7468] hover:border-[#0C0C10]"}`}>
            {f}
          </button>
        ))}
      </div>

      {/* Fee Table */}
      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "32px 1.5fr 1fr 0.5fr 80px 90px 100px 100px" }}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-3.5 h-3.5 accent-[#0C0C10]" />
          {["Student", "Fee Type", "Sem", "Amount", "Due Date", "Status", "Action"].map((h) => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
        ) : filtered.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "32px 1.5fr 1fr 0.5fr 80px 90px 100px 100px" }}>
            <input type="checkbox" checked={selected.includes(item._id)} onChange={() => toggleSelect(item._id)}
              disabled={item.status === "paid"} className="w-3.5 h-3.5 accent-[#0C0C10] disabled:opacity-30" />
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.student_name}</p>
              <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.student_email}</p>
            </div>
            <span className="font-['DM_Sans'] text-xs text-[#0C0C10]">{item.fee_type}</span>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.semester}</span>
            <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">₹{item.amount?.toLocaleString("en-IN")}</span>
            <span className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.due_date}</span>
            <span className={`font-['Syne'] font-bold text-[9px] uppercase tracking-wide px-2 py-1 border w-fit ${STATUS_STYLES[item.status] || ""}`}>{item.status}</span>
            {item.status === "paid" ? (
              <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/fee-receipt/${item._id}`, "_blank")}
                data-testid={`download-receipt-${i}`}
                className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#0A7AFF] border border-[#0A7AFF]/25 px-2.5 py-1.5 flex items-center gap-1 hover:bg-[#0A7AFF]/8 transition-colors">
                <Download className="w-3 h-3" /> PDF
              </button>
            ) : (
              <div className="flex gap-1">
                <button onClick={() => handlePay(item._id)} disabled={paying === item._id} data-testid={`pay-${i}`}
                  className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider bg-[#22C55E]/10 text-[#16A34A] border border-[#22C55E]/25 px-2 py-1.5 hover:bg-[#22C55E]/20 transition-colors disabled:opacity-50">
                  {paying === item._id ? "..." : "Pay"}
                </button>
                <button onClick={() => handleQRPay([item._id])} data-testid={`qr-pay-${i}`}
                  className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#7A7468] border border-[#D8D4CB] px-2 py-1.5 hover:bg-[#F0ECE4] transition-colors">
                  <QrCode className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Multi-select Pay Bar */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#0C0C10] text-white px-6 py-3 flex items-center gap-4 shadow-xl z-40" data-testid="pay-selected-bar">
            <span className="font-['DM_Sans'] text-sm">{selected.length} selected</span>
            <span className="font-['JetBrains_Mono'] font-bold text-lg">₹{selectedTotal.toLocaleString("en-IN")}</span>
            <button onClick={handlePaySelected} className="bg-[#22C55E] text-white font-['Syne'] font-bold text-xs uppercase tracking-wider px-5 py-2 hover:bg-[#16A34A] transition-colors">
              Pay Selected
            </button>
            <button onClick={() => handleQRPay(selected)} className="bg-white/10 text-white font-['Syne'] font-bold text-xs uppercase tracking-wider px-4 py-2 hover:bg-white/20 transition-colors flex items-center gap-1.5">
              <QrCode className="w-3.5 h-3.5" /> QR
            </button>
            <button onClick={() => setSelected([])} className="text-white/50 hover:text-white ml-2"><X className="w-4 h-4" /></button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Modal */}
      <AnimatePresence>
        {showQR && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && closeQR()}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-sm w-full p-8 text-center">
              {showQR.paid ? (
                <>
                  <CheckCircle className="w-16 h-16 text-[#22C55E] mx-auto mb-4" />
                  <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10] mb-2">Payment Received!</h3>
                  <p className="font-['JetBrains_Mono'] font-bold text-2xl text-[#0C0C10] mb-6">₹{(showQR.amount / 100).toLocaleString("en-IN")}</p>
                </>
              ) : (
                <>
                  <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10] mb-2">Scan to Pay</h3>
                  <p className="font-['JetBrains_Mono'] font-bold text-2xl text-[#0C0C10] mb-4">₹{(showQR.amount / 100).toLocaleString("en-IN")}</p>
                  {showQR.qr_code_url ? (
                    <img src={showQR.qr_code_url} alt="Payment QR Code" className="w-48 h-48 mx-auto mb-4 border border-[#E2DDD4]" />
                  ) : (
                    <div className="w-48 h-48 mx-auto mb-4 border border-[#E2DDD4] flex items-center justify-center bg-white">
                      <Loader2 className="w-8 h-8 animate-spin text-[#BDBAA8]" />
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Loader2 className="w-3 h-3 animate-spin text-[#BDBAA8]" />
                    <p className="font-['DM_Sans'] text-xs text-[#7A7468]">Waiting for payment...</p>
                  </div>
                  <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#BDBAA8] mb-4">Powered by Razorpay</p>
                </>
              )}
              <button onClick={closeQR} className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest px-8 py-3 hover:bg-[#1C1C22] transition-colors w-full">
                {showQR.paid ? "Done" : "Close"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Payment Link Modal */}
      <AnimatePresence>
        {showLinkModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowLinkModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Generate Payment Link</h3>
                <button onClick={() => setShowLinkModal(false)} className="text-[#BDBAA8] hover:text-[#0C0C10]"><X className="w-5 h-5" /></button>
              </div>

              {!linkResult ? (
                <>
                  {/* Tabs */}
                  <div className="flex gap-2 mb-6">
                    {[["fee", "Specific Fee"], ["student", "Student Fees"]].map(([key, label]) => (
                      <button key={key} onClick={() => setLinkTab(key)}
                        className={`font-['Syne'] font-bold text-[9px] uppercase tracking-widest px-4 py-2 border transition-all ${linkTab === key ? "bg-[#0C0C10] text-[#EAFF00] border-[#0C0C10]" : "border-[#D8D4CB] text-[#7A7468]"}`}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {linkTab === "fee" ? (
                    <div>
                      <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Select Pending Fee</label>
                      <select value={linkFeeId} onChange={(e) => setLinkFeeId(e.target.value)}
                        className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                        <option value="">Choose a fee...</option>
                        {data.filter((d) => d.status !== "paid").map((d) => (
                          <option key={d._id} value={d._id}>{d.student_name} — {d.fee_type} — ₹{d.amount?.toLocaleString("en-IN")}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Select Student</label>
                      <select value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)}
                        className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                        <option value="">Choose a student...</option>
                        {pendingStudents.map((email) => (
                          <option key={email} value={email}>{email}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <button onClick={handleCreateLink} disabled={linkLoading || (linkTab === "fee" ? !linkFeeId : !linkEmail)}
                    className="w-full mt-6 bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50">
                    {linkLoading ? "Generating..." : "Generate Link"}
                  </button>
                </>
              ) : (
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-[#22C55E] mx-auto mb-4" />
                  <p className="font-['DM_Sans'] text-sm text-[#7A7468] mb-2">Payment link created for</p>
                  <p className="font-['JetBrains_Mono'] font-bold text-xl text-[#0C0C10] mb-4">₹{(linkResult.amount / 100).toLocaleString("en-IN")}</p>
                  <div className="bg-white border border-[#E2DDD4] p-3 flex items-center gap-2 mb-4">
                    <span className="font-['DM_Sans'] text-xs text-[#0C0C10] truncate flex-1">{linkResult.short_url}</span>
                    <button onClick={copyLink} className="text-[#0A7AFF] hover:bg-[#0A7AFF]/10 p-1.5 transition-colors">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Pay your Acadence fees: ${linkResult.short_url}`)}`, "_blank")}
                    className="w-full border border-[#22C55E] text-[#16A34A] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3 hover:bg-[#22C55E]/10 transition-colors flex items-center justify-center gap-2">
                    <Send className="w-3.5 h-3.5" /> Send via WhatsApp
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Record Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Fee Record</h3>
                <button onClick={() => setShowModal(false)} className="text-[#BDBAA8] hover:text-[#0C0C10]"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleCreate} className="space-y-5">
                {[["student_name", "Student Name"], ["student_email", "Student Email"], ["amount", "Amount (₹)"]].map(([field, label]) => (
                  <div key={field}>
                    <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">{label}</label>
                    <input type={field === "amount" ? "number" : "text"} value={form[field]} onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))} required
                      className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                  </div>
                ))}
                <div>
                  <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Fee Type</label>
                  <select value={form.fee_type} onChange={(e) => setForm((p) => ({ ...p, fee_type: e.target.value }))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                    {["Tuition Fee", "Hostel Fee", "Library Fee", "Lab Fee", "Exam Fee"].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors">Add Record</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 px-4 py-3 shadow-lg z-50 font-['DM_Sans'] text-sm ${toast.type === "error" ? "bg-[#FF3636] text-white" : "bg-[#22C55E] text-white"}`}>
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
